import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import SEOHead from '../../components/SEO/SEOHead';

const PricingPage = () => {
    const [showTrialModal, setShowTrialModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [trialForm, setTrialForm] = useState({
        name: '',
        phone: ''
    });

    const handleTrialSubmit = async (e) => {
        e.preventDefault();
        
        // Validate required fields
        if (!trialForm.name.trim() || !trialForm.phone.trim()) {
            toast.error('Please fill in all required fields.');
            return;
        }
        
        setIsSubmitting(true);

        try {
            console.log('Submitting form data:', { name: trialForm.name, phone: trialForm.phone });
            
            const response = await fetch('https://formspree.io/f/mdkglkeg', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    name: trialForm.name.trim(),
                    phone: trialForm.phone.trim(),
                    message: 'Free trial request from pricing page',
                    _subject: `New Free Trial Request from ${trialForm.name}`,
                    _captcha: false
                })
            });

            const responseData = await response.json();
            console.log('Response:', response.status, responseData);

            if (response.ok) {
                setIsSuccess(true);
                toast.success('Your free trial request has been sent successfully! We will contact you within 24 hours.');
                setTrialForm({ name: '', phone: '' });
                // Keep modal open to show success message
                setTimeout(() => {
                    setShowTrialModal(false);
                    setIsSuccess(false);
                }, 3000);
            } else {
                throw new Error(responseData.error || 'Failed to send request');
            }
        } catch (error) {
            console.error('Form submission error:', error);
            toast.error('An error occurred while sending your request. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <SEOHead
                title="Pricing Plans - Fleet Management Software Saudi Arabia"
                description="Transparent pricing for Logistiat fleet management software. Starting at 2,990 SAR/year. 30-day free trial, no setup fees. Perfect for Saudi trucking companies."
                keywords="fleet management pricing, Saudi Arabia logistics software cost, TMS pricing KSA, transportation management system price, trucking software Saudi Arabia"
                canonicalUrl="/pricing"
                structuredData={{
                    "@context": "https://schema.org",
                    "@type": "Product",
                    "name": "Logistiat Fleet Management Software",
                    "description": "Complete fleet management system for Saudi Arabia trucking companies",
                    "brand": {
                        "@type": "Brand",
                        "name": "Logistiat"
                    },
                    "offers": [
                        {
                            "@type": "Offer",
                            "name": "Starter Plan",
                            "price": "2990",
                            "priceCurrency": "SAR",
                            "priceValidUntil": "2026-12-31",
                            "availability": "https://schema.org/InStock",
                            "description": "Perfect for small fleets up to 5 vehicles",
                            "url": "https://logistiat.app/pricing",
                            "itemCondition": "https://schema.org/NewCondition",
                            "shippingDetails": {
                                "@type": "OfferShippingDetails",
                                "shippingRate": {
                                    "@type": "MonetaryAmount",
                                    "value": "0",
                                    "currency": "SAR"
                                },
                                "shippingDestination": {
                                    "@type": "DefinedRegion",
                                    "addressCountry": "SA"
                                },
                                "deliveryTime": {
                                    "@type": "ShippingDeliveryTime",
                                    "handlingTime": {
                                        "@type": "QuantitativeValue",
                                        "minValue": 0,
                                        "maxValue": 0,
                                        "unitCode": "DAY"
                                    },
                                    "transitTime": {
                                        "@type": "QuantitativeValue",
                                        "minValue": 0,
                                        "maxValue": 0,
                                        "unitCode": "DAY"
                                    }
                                }
                            },
                            "hasMerchantReturnPolicy": {
                                "@type": "MerchantReturnPolicy",
                                "applicableCountry": "SA",
                                "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
                                "merchantReturnDays": 30,
                                "returnMethod": "https://schema.org/ReturnByMail",
                                "returnFees": "https://schema.org/FreeReturn"
                            }
                        },
                        {
                            "@type": "Offer",
                            "name": "Professional Plan",
                            "price": "4200",
                            "priceCurrency": "SAR",
                            "priceValidUntil": "2026-12-31",
                            "availability": "https://schema.org/InStock",
                            "description": "Best for growing fleets up to 25 vehicles",
                            "url": "https://logistiat.app/pricing",
                            "itemCondition": "https://schema.org/NewCondition",
                            "shippingDetails": {
                                "@type": "OfferShippingDetails",
                                "shippingRate": {
                                    "@type": "MonetaryAmount",
                                    "value": "0",
                                    "currency": "SAR"
                                },
                                "shippingDestination": {
                                    "@type": "DefinedRegion",
                                    "addressCountry": "SA"
                                },
                                "deliveryTime": {
                                    "@type": "ShippingDeliveryTime",
                                    "handlingTime": {
                                        "@type": "QuantitativeValue",
                                        "minValue": 0,
                                        "maxValue": 0,
                                        "unitCode": "DAY"
                                    },
                                    "transitTime": {
                                        "@type": "QuantitativeValue",
                                        "minValue": 0,
                                        "maxValue": 0,
                                        "unitCode": "DAY"
                                    }
                                }
                            },
                            "hasMerchantReturnPolicy": {
                                "@type": "MerchantReturnPolicy",
                                "applicableCountry": "SA",
                                "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
                                "merchantReturnDays": 30,
                                "returnMethod": "https://schema.org/ReturnByMail",
                                "returnFees": "https://schema.org/FreeReturn"
                            }
                        }
                    ]
                }}
            />
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>

                {/* Header */}
                <header className="bg-white shadow-sm border-b">
                    <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between items-center py-6">
                            <div className="flex items-center">
                                <Link to="/" className="text-2xl font-bold text-gray-900">Logistiat</Link>
                            </div>
                            <div className="hidden md:flex space-x-8">
                                <Link to="/features" className="text-gray-600 hover:text-gray-900 transition-colors">Features</Link>
                                <Link to="/pricing" className="text-blue-600 font-medium">Pricing</Link>
                                <Link to="/trip-costing" className="text-gray-600 hover:text-gray-900 transition-colors">Trip Costing</Link>
                                <Link to="/contact" className="text-gray-600 hover:text-gray-900 transition-colors">Contact</Link>
                                <Link to="/ar" className="text-gray-600 hover:text-gray-900 transition-colors">العربية</Link>
                            </div>
                            <div className="flex space-x-4">
                                <Link
                                    to="/login"
                                    className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                                >
                                    Login
                                </Link>
                            </div>
                        </div>
                    </nav>
                </header>

                {/* Hero Section */}
                <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h1 className="text-4xl md:text-6xl font-bold mb-6">
                            Simple, Transparent Pricing
                        </h1>
                        <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
                            Choose the perfect plan for your fleet size. All plans include core features with 30-day free trial. Save up to 20% with annual billing.
                        </p>
                        <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-2 text-blue-100">
                            <CheckCircleIcon className="w-5 h-5 mr-2" />
                            Annual pricing shown - Save 2 months compared to monthly
                        </div>
                    </div>
                </section>

                {/* Pricing Plans */}
                <section className="py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid md:grid-cols-3 gap-8">
                            {/* Starter Plan */}
                            <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-200">
                                <div className="text-center mb-8">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Starter</h3>
                                    <p className="text-gray-600 mb-4">Perfect for small fleets</p>
                                    <div className="text-4xl font-bold text-gray-900 mb-2">2,990 SAR</div>
                                    <p className="text-gray-600">per year</p>
                                    <p className="text-sm text-green-600 font-medium mt-2">Save 600 SAR vs monthly • Up to 5 vehicles</p>
                                </div>

                                <ul className="space-y-4 mb-8">
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Up to 5 vehicles
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Unlimited shipments
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Basic trip costing
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Driver management
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Basic reports
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Email support
                                    </li>
                                </ul>

                                <button
                                    onClick={() => setShowTrialModal(true)}
                                    className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                                >
                                    Start Free Trial
                                </button>
                            </div>

                            {/* Professional Plan */}
                            <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-blue-500 relative">
                                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                    <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold">Most Popular</span>
                                </div>

                                <div className="text-center mb-8">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Professional</h3>
                                    <p className="text-gray-600 mb-4">Best for growing fleets</p>
                                    <div className="text-4xl font-bold text-gray-900 mb-2">4,200 SAR</div>
                                    <p className="text-gray-600">per year</p>
                                    <p className="text-sm text-green-600 font-medium mt-2">Save 1,200 SAR vs monthly • Up to 25 vehicles</p>
                                </div>

                                <ul className="space-y-4 mb-8">
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Up to 25 vehicles
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Everything in Starter
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Advanced trip costing
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        PetroApp fuel integration
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        VAT & tax management
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Advanced analytics
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Priority support
                                    </li>
                                </ul>

                                <button
                                    onClick={() => setShowTrialModal(true)}
                                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                                >
                                    Start Free Trial
                                </button>
                            </div>

                            {/* Enterprise Plan */}
                            <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-200">
                                <div className="text-center mb-8">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Enterprise</h3>
                                    <p className="text-gray-600 mb-4">For large fleets</p>
                                    <div className="text-4xl font-bold text-gray-900 mb-2">Custom</div>
                                    <p className="text-gray-600">pricing</p>
                                    <p className="text-sm text-gray-500 mt-2">Unlimited vehicles</p>
                                </div>

                                <ul className="space-y-4 mb-8">
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Unlimited vehicles
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Everything in Professional
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Custom integrations
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Multi-company support
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Advanced user roles
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        API access
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Dedicated support
                                    </li>
                                </ul>

                                <Link
                                    to="/contact"
                                    className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-200 transition-colors text-center block"
                                >
                                    Contact Sales
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section className="py-20 bg-gray-50">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                                Frequently Asked Questions
                            </h2>
                        </div>

                        <div className="space-y-8">
                            <div className="bg-white p-6 rounded-lg shadow-sm">
                                <h3 className="text-xl font-semibold mb-3">Is there a free trial?</h3>
                                <p className="text-gray-600">Yes, all plans come with a 30-day free trial. No credit card required to start.</p>
                            </div>

                            <div className="bg-white p-6 rounded-lg shadow-sm">
                                <h3 className="text-xl font-semibold mb-3">Can I change plans anytime?</h3>
                                <p className="text-gray-600">Absolutely! You can upgrade or downgrade your plan at any time. Changes take effect immediately.</p>
                            </div>

                            <div className="bg-white p-6 rounded-lg shadow-sm">
                                <h3 className="text-xl font-semibold mb-3">What payment methods do you accept?</h3>
                                <p className="text-gray-600">We accept all major credit cards, bank transfers, and can arrange invoicing for enterprise customers.</p>
                            </div>

                            <div className="bg-white p-6 rounded-lg shadow-sm">
                                <h3 className="text-xl font-semibold mb-3">Is my data secure?</h3>
                                <p className="text-gray-600">Yes, we use enterprise-grade security with data encryption, regular backups, and comply with Saudi data protection regulations.</p>
                            </div>

                            <div className="bg-white p-6 rounded-lg shadow-sm">
                                <h3 className="text-xl font-semibold mb-3">Do you offer training and support?</h3>
                                <p className="text-gray-600">Yes, we provide comprehensive onboarding, training materials, and ongoing support in both Arabic and English.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-20 bg-blue-600 text-white">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6">
                            Ready to Get Started?
                        </h2>
                        <p className="text-xl mb-8">
                            Join hundreds of Saudi trucking companies using Logistiat to manage their fleets more efficiently.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={() => setShowTrialModal(true)}
                                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                            >
                                Start Free Trial
                            </button>
                            <Link
                                to="/contact"
                                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
                            >
                                Contact Sales
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Footer */}
                <footer className="bg-gray-900 text-white py-12">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid md:grid-cols-4 gap-8">
                            <div>
                                <h3 className="text-xl font-bold mb-4">Logistiat</h3>
                                <p className="text-gray-400">
                                    Complete fleet management solution for Saudi Arabia trucking companies.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold mb-4">Features</h4>
                                <ul className="space-y-2 text-gray-400">
                                    <li><Link to="/features" className="hover:text-white">Fleet Management</Link></li>
                                    <li><Link to="/trip-costing" className="hover:text-white">Trip Costing</Link></li>
                                    <li><Link to="/features" className="hover:text-white">Driver Management</Link></li>
                                    <li><Link to="/features" className="hover:text-white">Fuel Tracking</Link></li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="font-semibold mb-4">Company</h4>
                                <ul className="space-y-2 text-gray-400">
                                    <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
                                    <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
                                    <li><Link to="/ar" className="hover:text-white">العربية</Link></li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="font-semibold mb-4">Support</h4>
                                <ul className="space-y-2 text-gray-400">
                                    <li><Link to="/contact" className="hover:text-white">Help Center</Link></li>
                                    <li><Link to="/contact" className="hover:text-white">Contact Support</Link></li>
                                </ul>
                            </div>
                        </div>
                        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
                            <p>&copy; 2024 Logistiat. All rights reserved.</p>
                        </div>
                    </div>
                </footer>
            </div>

            {/* Trial Modal */}
            {showTrialModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-2xl font-bold text-gray-900">
                                Start Your Free Trial
                            </h3>
                            <button
                                onClick={() => setShowTrialModal(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>

                        {isSuccess ? (
                            <div className="text-center py-8">
                                <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                                <h4 className="text-xl font-semibold text-gray-900 mb-2">Success!</h4>
                                <p className="text-gray-600 mb-4">
                                    Your free trial request has been sent successfully!
                                </p>
                                <p className="text-sm text-gray-500">
                                    We will contact you within 24 hours to set up your account.
                                </p>
                            </div>
                        ) : (
                            <>
                                <p className="text-gray-600 mb-6">
                                    Get full access to Logistiat for 14 days. No credit card required.
                                </p>

                                <form onSubmit={handleTrialSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Name or Company Name *
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={trialForm.name}
                                    onChange={(e) => setTrialForm({ ...trialForm, name: e.target.value })}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                    placeholder="Your name or company name"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Phone Number *
                                </label>
                                <input
                                    type="tel"
                                    required
                                    value={trialForm.phone}
                                    onChange={(e) => setTrialForm({ ...trialForm, phone: e.target.value })}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                    placeholder="+966 50 000 0000"
                                />
                            </div>

                            <div className="flex gap-3">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? 'Sending...' : 'Start Trial'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowTrialModal(false)}
                                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                                </form>

                                <p className="text-sm text-gray-500 mt-4 text-center">
                                    We'll contact you within 24 hours to set up your account
                                </p>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default PricingPage;